import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useApiClient } from "../../../apiClient";
import { GetCurrentOrganizationDto } from "../../../models/organization/dto/get-current-organization.dto";
import { GET_PENDING_INVITATIONS_QUERY_KEY } from "./useGetPendingInvitations";

export const GET_CURRENT_ORGANIZATION_QUERY_KEY = ['currentOrganization'];

/**
 * Resolves the logged-in admin's HOME organization (their oldest membership).
 * Used by features that intentionally act on the admin's own org regardless of
 * the org picker (court calendar, coach invite, blockade modal). Do NOT use
 * this for the org-picker-driven Organization page — see
 * useGetSelectedOrganizationView, which scopes to the picked org and works for
 * elevated/platform admins who are not members of the org.
 */
export const useGetCurrentOrganization = () => {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    const result = useQuery({
        queryKey: GET_CURRENT_ORGANIZATION_QUERY_KEY,
        queryFn: async () => {
            return apiClient.fetchApi<GetCurrentOrganizationDto, {}>({ method: 'GET', endpoint: `organizations/get-current-organization` });
        },
    });

    useEffect(() => {
        if (result.data?.invitations && result.data.organization.id) {
            queryClient.setQueryData(GET_PENDING_INVITATIONS_QUERY_KEY(result.data.organization.id), result.data.invitations);
        }
    }, [result.data, queryClient]);

    return result;
};

export const GET_SELECTED_ORGANIZATION_VIEW_QUERY_KEY = (organizationId: string) => ['currentOrganization', organizationId];

/**
 * Resolves a SPECIFIC organization (the one chosen in the org picker), scoped
 * by its id. Unlike useGetCurrentOrganization it does not depend on the caller
 * being a member, so a superadmin switching orgs sees the selected org's
 * members instead of an error. Disabled until an organizationId is available,
 * so it never falls back to the home-org endpoint (which 500s for a superadmin
 * who has no membership).
 */
export const useGetSelectedOrganizationView = (organizationId?: string) => {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    const result = useQuery({
        queryKey: GET_SELECTED_ORGANIZATION_VIEW_QUERY_KEY(organizationId ?? 'none'),
        enabled: !!organizationId,
        queryFn: async () => {
            return apiClient.fetchApi<GetCurrentOrganizationDto, {}>({ method: 'GET', endpoint: `organizations/get-current-organization?organizationId=${encodeURIComponent(organizationId!)}` });
        },
    });

    useEffect(() => {
        if (result.data?.invitations && result.data.organization.id) {
            queryClient.setQueryData(GET_PENDING_INVITATIONS_QUERY_KEY(result.data.organization.id), result.data.invitations);
        }
    }, [result.data, queryClient]);

    return result;
};